import { AxiosRequestConfig } from 'axios';
import { BASE_UAPI_URL } from '../../constants/commonStrings.constant';
import { apiService } from '../api.service';

export type GetMassupdateIdPathParams = {
  /**
   * @description Mass Update ID
   * @type string
   */
  id: number;
};

export type PostMassupdateMutationRequest = {
  /**
   * @type integer | undefined
   */
  massupdate_id?: number | undefined;
  /**
   * @type string | undefined
   */
  massupdate_name?: string | undefined;
  /**
   * @type object | undefined
   */
  'queryassignees[]'?: string[] | undefined;
  /**
   * @type array | undefined
   */
  'querycustomers[]'?: string[] | undefined;
  /**
   * @type array | undefined
   */
  'querystatus[]'?: string[] | undefined;
  /**
   * @type array | undefined
   */
  'querytypes[]'?: string[] | undefined;
  /**
   * @type integer | undefined
   */
  updateassignee?: number | undefined;
  /**
   * @type integer | undefined
   */
  updatecustomer?: number | undefined;
  /**
   * @type integer | undefined
   */
  updatestatus?: number | undefined;
  /**
   * @type integer | undefined
   */
  updatetype?: number | undefined;
};

export type DslibTicketQuery = {
  /**
   * @type array | undefined
   */
  filterAssignee?: number[] | undefined;
  /**
   * @type boolean | undefined
   */
  filterClosed?: boolean | undefined;
  /**
   * @type array | undefined
   */
  filterCustomer?: number[] | undefined;
  /**
   * @type array | undefined
   */
  filterPriority?: number[] | undefined;
  /**
   * @type array | undefined
   */
  filterRaisedBy?: number[] | undefined;
  /**
   * @type array | undefined
   */
  filterStatus?: number[] | undefined;
  /**
   * @description (kwt 02/13/2023) added
   * @type string | undefined
   */
  filterStatusCategory?: string | undefined;
  /**
   * @type array | undefined
   */
  filterType?: number[] | undefined;
  /**
   * @description (kwt 02/22/2023) clauses from saved user filters
   * @type array | undefined
   */
  filterWhereClauses?: string[] | undefined;
  /**
   * @description (kwt 03/09/2023) added
   * @type boolean | undefined
   */
  includeCustomFieldJson?: boolean | undefined;
  /**
   * @type array | undefined
   */
  sort?: DslibSortColumn[] | undefined;
};

export type DslibSortColumn = {
  /**
   * @type integer | undefined
   */
  column?: DslibSortColumnId | undefined;
  /**
   * @type boolean | undefined
   */
  direction?: boolean | undefined;
};

export type DslibUpdateColumn = {
  /**
   * @description Will be used to determine the datatype of the value and\nthe column we are updating in the query
   */
  column?: DslibUpdateColumnId | undefined;
  value?: unknown | undefined;
};

export const dslibUpdateColumnIdEnum = {
  _updateTop: 0,
  UpdateStatus: 1,
  UpdateType: 2,
  UpdateAssignee: 3,
  UpdateCustomer: 4,
} as const;
export type DslibUpdateColumnIdEnum = (typeof dslibUpdateColumnIdEnum)[keyof typeof dslibUpdateColumnIdEnum];
export type DslibUpdateColumnId = DslibUpdateColumnIdEnum;

export const dslibSortColumnIdEnum = {
  _indexTop: 0,
  SortStatus: 1,
  SortAssignee: 2,
  SortCustomer: 3,
  SortRaisedBy: 4,
  SortType: 5,
  SortPriority: 6,
  SortCreateTime: 7,
  SortLastUpdated: 8,
  SortDeleteTime: 9,
  SortTitle: 10,
  SortID: 11,
} as const;
export type DslibSortColumnIdEnum = (typeof dslibSortColumnIdEnum)[keyof typeof dslibSortColumnIdEnum];
export type DslibSortColumnId = DslibSortColumnIdEnum;

export type PostMassupdateIdExecutePathParams = {
  /**
   * @description Mass Update ID
   * @type string
   */
  id: string;
};

export type PutMassupdateIdPathParams = {
  /**
   * @description Mass Update ID
   * @type string
   */
  id: string;
};

export type DeleteMassupdateIdPathParams = {
  /**
   * @description Mass Update ID
   * @type string
   */
  id: string;
};

const BatchUpdatesService = () => {

  /**
 * @description Retrieves all mass updates
 * @summary Get all mass updates
 * @link /massupdate
 */
  const getBatchUpdate = async (
    // params: GetMassupdateQueryResponse,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    return apiService({
      method: 'get',
      url: BASE_UAPI_URL + '/massupdate',
      ...config,
    });
  };

  /**
 * @description Retrieves the affect count of a mass update
 * @summary Get the affect count of a mass update
 * @link /massupdate/affectcount
 */
  const getBatchUpdateAffectCount = async (
    params: DslibTicketQuery,
    config?: Partial<AxiosRequestConfig>,

  ): Promise<any> => {
    return apiService({
      method: 'post',
      url: BASE_UAPI_URL + '/massupdate/affectcount',
      data: params,
      ...config,
    });

  };


  /**
 * @description Retrieves a mass update by ID
 * @summary Get a mass update
 * @link /massupdate/:id
 */
  const getBatchUpdateById = async (
    params: GetMassupdateIdPathParams,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    const { id } = params
    return apiService({
      method: 'get',
      url: BASE_UAPI_URL + '/massupdate?id=' + id,
      // params,
      ...config,
    });
  };

  /**
 * @description Creates a new mass update
 * @summary Create a mass update
 * @link /massupdate
 */
  const postBatchUpdate = async (
    data: PostMassupdateMutationRequest,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    return apiService({
      method: 'post',
      url: BASE_UAPI_URL + '/massupdate',
      data,
      ...config,
    });
  };

  /**
 * @description Executes a mass update by ID
 * @summary Execute a mass update
 * @link /massupdate/:id/execute
 */
  const executeBatchUpdateById = async (
    params: PostMassupdateIdExecutePathParams,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    const { id } = params
    return apiService({
      method: 'post',
      url: BASE_UAPI_URL + '/massupdate?id=' + id + '/execute',
      ...config,
    });
  };

  /**
 * @description Updates an existing mass update by ID
 * @summary Update a mass update
 * @link /massupdate/:id
 */
  const putBatchUpdateById = async (
    params: PutMassupdateIdPathParams,
    data: PostMassupdateMutationRequest,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    const { id } = params
    console.log('params : ', params)
    console.log('data : ', data)
    return apiService({
      method: 'put',
      url: BASE_UAPI_URL + '/massupdate?id=' + id,
      data,
      ...config,
    });
  };

  /**
 * @description Deletes a mass update by ID
 * @summary Delete a mass update
 * @link /massupdate/:id
 */
  const deleteBatchUpdateById = async (
    params: DeleteMassupdateIdPathParams,
    config?: Partial<AxiosRequestConfig>,
  ): Promise<any> => {
    const { id } = params
    return apiService({
      method: 'delete',
      url: BASE_UAPI_URL + '/massupdate?id=' + id,
      ...config,
    });
  };

  return {
    getBatchUpdate,
    getBatchUpdateAffectCount,
    getBatchUpdateById,
    postBatchUpdate,
    executeBatchUpdateById,
    putBatchUpdateById,
    deleteBatchUpdateById
  };
};

export default BatchUpdatesService;
